/* Rosterpress cart drawer: pure grouping/removal/progress logic. Shared by the drawer script (window.RPCartCore) and node tests.
   Input items = Shopify /cart.js item objects (key, variant_id, product_handle, quantity, properties) plus a boolean
   `apparel` supplied by Liquid (true when the product carries the rp-apparel/rp-premium tag). */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api; else root.RPCartCore = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  function isFee(item, fees, which) {
    return item.product_handle === fees[which];
  }

  /* Cart-level add-on lines toggled in the drawer: Shipping Protection and Rush Order (fees.rush is optional). */
  function isAddon(item, fees) {
    return isFee(item, fees, 'prot') || (!!fees.rush && isFee(item, fees, 'rush'));
  }

  function personalizedShirt(item) {
    var p = item.properties || {};
    return !!(String(p.Name || '').trim() || String(p['Team Name'] || '').trim());
  }

  /* Group cart lines: bundles keyed by properties._bundle (order of first appearance), each with its shirt lines and
     (if any) its matching Personalization fee line; standalone (non-bundle, non-fee, non-protection) lines; the single
     Protection line if present; and any Personalization fee line left without shirts (all its shirts were removed). */
  function group(items, fees) {
    var bundles = [];
    var byId = {};
    var singles = [];
    var protection = null;
    var rush = null;
    var pendingFees = [];

    items.forEach(function (item) {
      if (isFee(item, fees, 'prot')) {
        protection = item;
        return;
      }
      if (isAddon(item, fees)) {
        rush = item;
        return;
      }
      if (isFee(item, fees, 'pers')) {
        pendingFees.push(item);
        return;
      }
      var bId = item.properties && item.properties._bundle;
      if (bId) {
        var b = byId[bId];
        if (!b) {
          b = { id: bId, lines: [], fee: null };
          byId[bId] = b;
          bundles.push(b);
        }
        b.lines.push(item);
      } else {
        singles.push(item);
      }
    });

    var orphanFees = [];
    pendingFees.forEach(function (fee) {
      var bId = fee.properties && fee.properties._bundle;
      var b = bId && byId[bId];
      if (b && b.lines.length) {
        b.fee = fee;
      } else {
        orphanFees.push(fee);
      }
    });

    return { bundles: bundles, singles: singles, protection: protection, rush: rush, orphanFees: orphanFees };
  }

  /* Shirt count = qty of bundle shirt lines (excl. fee/protection) + qty of non-bundle lines flagged `apparel`. */
  function shirtCount(items, fees) {
    return items.reduce(function (n, item) {
      if (isFee(item, fees, 'pers') || isAddon(item, fees)) return n;
      var bId = item.properties && item.properties._bundle;
      if (bId || item.apparel) return n + (item.quantity || 0);
      return n;
    }, 0);
  }

  function findByKey(items, key) {
    for (var i = 0; i < items.length; i++) if (items[i].key === key) return items[i];
    return null;
  }

  /* Protection and Rush go only when no NON-FEE line (anything but the Personalization/Protection/Rush products)
     survives the updates — the same rule as the Liquid rp_other count that decides whether their rows are shown. */
  function dropProtectionIfAlone(items, updates, fees) {
    var addons = items.filter(function (i) { return isAddon(i, fees); });
    if (!addons.length) return;
    var left = items.some(function (i) {
      if (isFee(i, fees, 'pers') || isAddon(i, fees)) return false;
      var q = Object.prototype.hasOwnProperty.call(updates, i.key) ? updates[i.key] : i.quantity;
      return q > 0;
    });
    if (!left) addons.forEach(function (a) { updates[a.key] = 0; });
  }
  /* Remove one shirt line. If it was personalized, drop the bundle's Personalization fee quantity by this shirt's
     qty (0 removes the fee line). If no non-fee line remains in the cart afterwards, also drop the Protection line. */
  function removeShirt(items, key, fees) {
    var item = findByKey(items, key);
    var updates = {};
    updates[key] = 0;
    if (!item) return { updates: updates };

    if (personalizedShirt(item)) {
      var bId = item.properties && item.properties._bundle;
      var fee = items.find(function (i) { return isFee(i, fees, 'pers') && i.properties && i.properties._bundle === bId; });
      if (fee) {
        var next = (fee.quantity || 0) - (item.quantity || 0);
        updates[fee.key] = next > 0 ? next : 0;
      }
    }

    dropProtectionIfAlone(items, updates, fees);
    return { updates: updates };
  }

  /* Remove an entire bundle (its shirt lines + fee line). If no non-fee line remains, also drop Protection. */
  function removeBundle(items, bundleId, fees) {
    var updates = {};
    items.forEach(function (item) {
      var bId = item.properties && item.properties._bundle;
      if (bId !== bundleId) return;
      updates[item.key] = 0;
    });

    dropProtectionIfAlone(items, updates, fees);

    return { updates: updates };
  }

  /* ON: add the add-on variant unless its line already exists (no-op then). OFF: zero its line. */
  function toggleAddon(items, on, fees, which, variantId) {
    var line = items.find(function (i) { return isFee(i, fees, which); });
    if (on) {
      if (line) return {};
      return { add: [{ id: variantId, quantity: 1 }] };
    }
    if (!line) return {};
    var updates = {};
    updates[line.key] = 0;
    return { updates: updates };
  }

  function toggleProtection(items, on, fees, protVariantId) {
    return toggleAddon(items, on, fees, 'prot', protVariantId);
  }

  function toggleRush(items, on, fees, rushVariantId) {
    return toggleAddon(items, on, fees, 'rush', rushVariantId);
  }

  /* Free-shipping progress: unlocked at `qty` shirts or `amount` subtotal (cents). */
  function progress(count, subtotal, qty, amount) {
    qty = qty == null ? 4 : qty;
    amount = amount == null ? 10000 : amount;
    var unlocked = count >= qty || subtotal >= amount;
    var missing = unlocked ? 0 : Math.max(0, qty - count);
    var pct = unlocked ? 100 : Math.min(100, Math.round((count / qty) * 100));
    return { unlocked: unlocked, missing: missing, pct: pct };
  }

  return {
    group: group,
    shirtCount: shirtCount,
    removeShirt: removeShirt,
    removeBundle: removeBundle,
    toggleProtection: toggleProtection,
    toggleRush: toggleRush,
    progress: progress
  };
});
